import { Injectable } from '@angular/core';
import { AuthService } from '../../../auth/auth.service';
import { MultiBuyerCmpAuth, ServiceBuyer } from '../../../auth/auth.interface';

interface BuyerContext {
  readonly token: string;
  readonly companyId: number;
  readonly userId: number;
}

class MultiBuyerContext implements BuyerContext {
  constructor(
    private readonly _company: MultiBuyerCmpAuth,
    private readonly _fallbackToken: string,
    private readonly _fallbackUserId: number
  ) {}

  get token(): string {
    return this._company.token ?? this._fallbackToken;
  }

  get companyId(): number {
    return this._company.id;
  }

  get userId(): number {
    return this._company.userId ?? this._fallbackUserId;
  }
}

class ServiceBuyerContext implements BuyerContext {
  constructor(
    private readonly _serviceBuyer: ServiceBuyer,
    private readonly _fallbackToken: string,
    private readonly _userId: number
  ) {}

  get token(): string {
    return this._serviceBuyer.token ?? this._fallbackToken;
  }

  get companyId(): number {
    return this._serviceBuyer.id;
  }

  get userId(): number {
    return this._userId;
  }
}

class DefaultBuyerContext implements BuyerContext {
  constructor(
    private readonly _token: string,
    private readonly _companyId: number,
    private readonly _userId: number
  ) {}

  get token(): string {
    return this._token;
  }

  get companyId(): number {
    return this._companyId;
  }

  get userId(): number {
    return this._userId;
  }
}

@Injectable({ providedIn: 'root' })
export class BuyerContextService {
  constructor(private _auth: AuthService) {}

  private _resolveContext(): BuyerContext {
    const fallbackToken = this._auth.token!;
    const userId = this._auth.user!.id;

    if (this._auth.isSelectedMultiBuyerCompany()) {
      return new MultiBuyerContext(
        this._auth.selectedMultiBuyerCompany!,
        fallbackToken,
        userId
      );
    }

    if (this._auth.isLoggedInAsServiceBuyer()) {
      return new ServiceBuyerContext(
        this._auth.serviceBuyer!,
        fallbackToken,
        userId
      );
    }

    return new DefaultBuyerContext(fallbackToken, this._auth.user!.cmp, userId);
  }

  get token(): string {
    return this._resolveContext().token;
  }

  get companyId(): number {
    return this._resolveContext().companyId;
  }

  get userId(): number {
    return this._resolveContext().userId;
  }

  get apiUserId(): number {
    if (this._auth.isSelectedMultiBuyerCompany()) {
      return (
        this._auth.selectedMultiBuyerCompany!.apiUserId ?? this._auth.user!.id
      );
    }

    return this._auth.user!.id;
  }
}
